import React, { useState, useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useFlagr } from 'api/flagr';
import { Translate as T } from 'react-redux-i18n';
import { PERSONAL_CHECKS_ANALYTICS_EVENTS } from 'lib/analytics';
import { trackAnalyticsEvent } from 'actions/analytics';
import styles from './PersonalChecksBanner.module.scss';
import { FLAGR_KEYS } from '../../constants';

const BANNER_DISMISSED_KEY = 'personalChecksBannerDismissed';
const BANNER_VIEWED_KEY = 'personalChecksBannerViewed';

// Types
interface Report {
  accountId: string;
  candidateId: string;
  [key: string]: any;
}

const generatePersonalChecksUrl = (reports: Report[]): string => {
  const baseUrl = 'https://checkr.com/profiles';

  const params = new URLSearchParams({
    utm_source: 'candidate_portal',
    utm_medium: 'personal_checks_banner',
    utm_campaign: 'personal_checks_banner',
    utm_content: reports[0]?.candidateId || 'no_candidate_id',
  });

  return `${baseUrl}?${params.toString()}`;
};

const hasBlockedAccountReports = (
  reports: Report[],
  blockedAccountIds: string[],
) => {
  if (!reports || !blockedAccountIds) return true;
  const blockedAccountIdsSet = new Set(blockedAccountIds);
  return reports.some((report: any) => {
    return blockedAccountIdsSet.has(report.accountId);
  });
};

const hasAllReportsCompleted = (reports: Report[]) => {
  return reports.every((report: any) => {
    return (
      report?.displayStatus?.toLowerCase() === 'complete' &&
      report?.status?.toLowerCase() === 'clear'
    );
  });
};

export default function PersonalChecksBanner() {
  const state = useStore().getState();
  const dispatch = useDispatch();
  const { reports, applicantId } = state.portal;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true',
  );

  const { flag, isFetched: flagIsLoaded } = useFlagr({
    context: {
      locationHost: window.location.host,
      applicantId,
    },
    id: FLAGR_KEYS.personalChecksBannerFlagKey,
    entity: { id: applicantId, type: 'applicantId' },
    fallbackVariant: 'off',
  });

  const {
    variantKey: personalChecksBannerVariantKey,
    variantAttachment: personalChecksBannerAttachments,
  } = flag;

  const blockedAccountIds = personalChecksBannerAttachments?.blockedAccountIds;
  const bannerDismissed = localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
  const bannerHasBeenViewed =
    localStorage.getItem(BANNER_VIEWED_KEY) === 'true';

  useEffect(() => {
    if (
      !flagIsLoaded ||
      personalChecksBannerVariantKey === 'off' ||
      bannerDismissed ||
      hasBlockedAccountReports(reports, blockedAccountIds) ||
      !hasAllReportsCompleted(reports)
    ) {
      setDismissed(true);
      return;
    }

    // Set dismissed to false if no blocked accounts were matched
    setDismissed(false);
  }, [
    applicantId,
    bannerDismissed,
    blockedAccountIds,
    dispatch,
    flagIsLoaded,
    personalChecksBannerVariantKey,
    reports,
  ]);

  useEffect(() => {
    if (!dismissed && !bannerHasBeenViewed) {
      dispatch(
        trackAnalyticsEvent(
          PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_BANNER_VIEWED,
          {
            applicantId,
            reportIds: reports.map((report: any) => report.id),
          },
        ),
      );
      localStorage.setItem(BANNER_VIEWED_KEY, 'true');
    }
  }, [dismissed, bannerHasBeenViewed, dispatch, applicantId, reports]);

  if (dismissed || !flagIsLoaded || personalChecksBannerVariantKey === 'off') {
    return null;
  }

  const handleDismissClick = () => {
    trackAnalyticsEvent(
      PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_DISMISS_CLICKED,
    );
    setDismissed(true);
  };

  const handleCtaClick = () => {
    trackAnalyticsEvent(
      PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_BANNER_CTA_CLICKED,
    );
  };

  return (
    <div className={styles.personalChecksBanner}>
      <div className={styles.personalChecksBannerContent}>
        <div className={styles.personalChecksBannerHeader}>
          <h2 className={styles.personalChecksBannerTitle}>
            <T value='components.PersonalChecks.banner.title' />
          </h2>
          <button
            className={styles.personalChecksBannerDismiss}
            type='button'
            onClick={handleDismissClick}
          >
            <T value='components.PersonalChecks.banner.buttons.dismiss' />
          </button>
        </div>
        <div className={styles.personalChecksBannerBody}>
          <p className={styles.personalChecksBannerDescription}>
            <T value='components.PersonalChecks.banner.body' />
          </p>
          <a
            href={generatePersonalChecksUrl(reports)}
            target='_blank'
            rel='noopener noreferrer'
            className={styles.personalChecksBannerCta}
            onClick={handleCtaClick}
          >
            <T value='components.PersonalChecks.banner.buttons.cta' />
          </a>
        </div>
      </div>
    </div>
  );
}
